import { Head, useForm, Link } from '@inertiajs/react'
import AuthLayout from '../../components/AuthLayout'
import ClientAuthLayout from '../../components/ClientAuthLayout'

interface Props {
  errors: {
    email?: string
    base?: string
  }
  notice?: string
  csrf_token?: string
  client_flow?: boolean
}

export default function ForgotPassword({ errors, notice, csrf_token, client_flow = false }: Props) {
  const { data, setData, post, processing, transform } = useForm({
    account: {
      email: '',
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Carry the freshly-rendered CSRF token explicitly so the request never
    // depends on a possibly-stale XSRF-TOKEN cookie (see Login.tsx).
    transform((current) => ({ ...current, authenticity_token: csrf_token ?? '' }))
    post(client_flow ? '/clients/password' : '/users/password')
  }

  if (client_flow) {
    return (
      <>
        <Head title="Forgot Password - Pinnacle Navigator" />
        <ClientAuthLayout>
          {notice && (
            <div className="mb-6 rounded border-l-[3px] border-[#15532E] bg-[#E7F0EB] p-3">
              <p className="text-base text-text-primary">{notice}</p>
            </div>
          )}
          {errors.base && (
            <div className="mb-6 rounded border-l-[3px] border-[#C7161E] bg-[#F9E8E8] p-3" role="alert">
              <p className="text-base text-text-primary">{errors.base}</p>
            </div>
          )}
          <div className="mb-8 text-center">
            <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-text-primary">Client access</p>
            <h1 className="font-serif text-3xl italic text-text-primary">Forgot your password?</h1>
            <p className="mt-3 text-sm leading-6 text-text-primary">Enter your email and we&apos;ll send reset instructions.</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="client-email" className="mb-2 block text-xs font-bold uppercase tracking-[0.1em] text-text-primary">Email</label>
              <input id="client-email" type="email" value={data.account.email} onChange={e => setData('account', { ...data.account, email: e.target.value })} className="input w-full" placeholder="you@example.com" required autoComplete="email" autoFocus />
              {errors.email && <p className="mt-1 text-sm text-text-primary">{errors.email}</p>}
            </div>
            <button type="submit" disabled={processing} className="w-full rounded-button bg-pinnacle-red px-6 py-3.5 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-pinnacle-red-dark disabled:cursor-not-allowed disabled:opacity-70">
              {processing ? 'Sending...' : 'Send reset instructions'}
            </button>
          </form>
          <div className="mt-6 text-center">
            <Link href="/clients/sign_in" className="text-base font-medium text-text-primary underline underline-offset-4 hover:text-[#C7161E]">Back to sign in</Link>
          </div>
          <p className="mt-5 text-center text-sm text-text-primary">Need help? <a href="mailto:climb@pinnacle.guide" className="font-medium underline underline-offset-4 hover:text-[#C7161E]">climb@pinnacle.guide</a></p>
        </ClientAuthLayout>
      </>
    )
  }

  return (
    <>
      <Head title="Forgot Password - Pinnacle" />
      <AuthLayout>
        {notice && (
          <div
            className="mb-6 p-3 rounded"
            style={{
              background: 'rgba(21, 83, 46, 0.08)',
              borderLeft: '3px solid #15532E',
            }}
          >
            <p className="font-sans text-base text-text-primary">{notice}</p>
          </div>
        )}

        {errors.base && (
          <div
            className="mb-6 p-3 rounded"
            style={{
              background: 'rgba(237, 28, 36, 0.08)',
              borderLeft: '3px solid #ED1C24',
            }}
          >
            <p className="font-sans text-base text-text-primary">{errors.base}</p>
          </div>
        )}

        <p className="font-serif italic text-2xl text-text-primary text-center mb-2"
           style={{ color: '#1B1B1B' }}>
          Forgot your password?
        </p>
        <p className="font-sans text-sm text-text-muted text-center mb-8">
          Enter your email and we&apos;ll send you instructions to reset it.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="block font-sans text-xs font-bold uppercase tracking-[0.1em] text-text-primary mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={data.account.email}
              onChange={e => setData('account', { ...data.account, email: e.target.value })}
              className="input w-full"
              placeholder="you@example.com"
              required
              autoComplete="email"
              autoFocus
            />
            {errors.email && (
              <p className="mt-1 text-sm text-[#ED1C24]">{errors.email}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={processing}
            className="w-full bg-pinnacle-red hover:bg-pinnacle-red-dark text-white font-bold text-sm uppercase tracking-wider py-3.5 px-6 rounded-button transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {processing ? 'SENDING...' : 'SEND RESET INSTRUCTIONS'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link href="/users/sign_in" className="text-base font-sans text-text-primary hover:underline font-medium">
            Back to sign in
          </Link>
        </div>

        <div className="mt-4 text-center">
          <p className="font-sans text-sm text-text-muted">
            Need help?{' '}
            <a href="mailto:climb@pinnacle.guide" className="text-text-primary hover:underline">
              climb@pinnacle.guide
            </a>
          </p>
        </div>
      </AuthLayout>
    </>
  )
}
